import { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import parse from "html-react-parser";
import { registerNlpStart } from '../../utils/utils';
import { useTranslation } from 'react-i18next';

export interface AnalyzedTextProps {
    task_id: string;
    analyzedText: any;
    saveFormattedTextCallback: CallableFunction;
    language?: string;
}

interface FilterItem {
    id: string;
    category: string;
    option: string;
    color: string;
    size: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
}

export default function AnalyzedTextRussian(props: AnalyzedTextProps) {
    const { t } = useTranslation();

    const [allText, setAllText] = useState('');
    const [processingEmpty, setProcessingEmpty] = useState(false);
    const [nlpCategories, setNlpCategories] = useState<any>({});
    const [filters, setFilters] = useState<FilterItem[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [expandedPickerGroup, setExpandedPickerGroup] = useState<string | null>(null);
    const [expandedPickerSubgroup, setExpandedPickerSubgroup] = useState<string | null>(null);

    // --- Feature helper (Russian: operates on features string, not token) ---

    const hasFeature = (featuresStr: string, featureName: string, featureValue: string) => {
        if (!featuresStr || featuresStr === '_' || featuresStr === 'NoFeats') return false;
        const features = featuresStr.split('|');
        return features.some(f => f === `${featureName}=${featureValue}`);
    }

    // --- Color/Size decode ---

    function decodeColor(colorcode: string) {
        switch (colorcode) {
            case 'blue-violet': return 'blueviolet';
            case 'brown': return 'brown';
            case 'coral': return 'coral';
            case 'dark-blue': return 'darkblue';
            case 'dark-cyan': return 'darkcyan';
            case 'crimson': return 'crimson';
            case 'dark-green': return 'darkgreen';
            case 'dark-salmon': return 'darksalmon';
            case 'dark-orange': return 'darkorange';
            case 'cadet-blue': return 'cadetblue';
            case 'chocolate': return 'chocolate';
            case 'dark-magenta': return 'darkmagenta';
            default: return '';
        }
    }

    function decodeSize(sizecode: string) {
        switch (sizecode) {
            case 'big': return '1.4em';
            case 'huge': return '1.8em';
            default: return '';
        }
    }

    // --- Word matching ---

    const wordMatchesFilter = (word: any, filter: FilterItem): boolean => {
        switch (filter.category) {
            case 'nouns':
                if (word.upos !== 'NOUN') return false;
                switch (filter.option) {
                    case 'nouns-gender-fem': return hasFeature(word.features, 'Gender', 'Fem');
                    case 'nouns-gender-masc': return hasFeature(word.features, 'Gender', 'Masc');
                    case 'nouns-gender-neut': return hasFeature(word.features, 'Gender', 'Neut');
                    case 'nouns-animacy-anim': return hasFeature(word.features, 'Animacy', 'Anim');
                    case 'nouns-animacy-inan': return hasFeature(word.features, 'Animacy', 'Inan');
                    case 'nouns-number-sing': return hasFeature(word.features, 'Number', 'Sing');
                    case 'nouns-number-plur': return hasFeature(word.features, 'Number', 'Plur');
                    case 'nouns-case-nom': return hasFeature(word.features, 'Case', 'Nom');
                    case 'nouns-case-gen': return hasFeature(word.features, 'Case', 'Gen');
                    case 'nouns-case-acc': return hasFeature(word.features, 'Case', 'Acc');
                    case 'nouns-case-dat': return hasFeature(word.features, 'Case', 'Dat');
                    case 'nouns-case-ins': return hasFeature(word.features, 'Case', 'Ins');
                    case 'nouns-case-loc': return hasFeature(word.features, 'Case', 'Loc');
                    case 'all':
                    default: return true;
                }

            case 'properNouns':
                if (word.upos !== 'PROPN') return false;
                switch (filter.option) {
                    case 'properNouns-gender-fem': return hasFeature(word.features, 'Gender', 'Fem');
                    case 'properNouns-gender-masc': return hasFeature(word.features, 'Gender', 'Masc');
                    case 'properNouns-gender-neut': return hasFeature(word.features, 'Gender', 'Neut');
                    case 'properNouns-animacy-anim': return hasFeature(word.features, 'Animacy', 'Anim');
                    case 'properNouns-animacy-inan': return hasFeature(word.features, 'Animacy', 'Inan');
                    case 'properNouns-number-sing': return hasFeature(word.features, 'Number', 'Sing');
                    case 'properNouns-number-plur': return hasFeature(word.features, 'Number', 'Plur');
                    case 'properNouns-case-nom': return hasFeature(word.features, 'Case', 'Nom');
                    case 'properNouns-case-gen': return hasFeature(word.features, 'Case', 'Gen');
                    case 'properNouns-case-acc': return hasFeature(word.features, 'Case', 'Acc');
                    case 'properNouns-case-dat': return hasFeature(word.features, 'Case', 'Dat');
                    case 'properNouns-case-ins': return hasFeature(word.features, 'Case', 'Ins');
                    case 'properNouns-case-loc': return hasFeature(word.features, 'Case', 'Loc');
                    case 'all':
                    default: return true;
                }

            case 'pronouns':
                if (word.upos !== 'PRON') return false;
                switch (filter.option) {
                    case 'pronouns-gender-fem': return hasFeature(word.features, 'Gender', 'Fem');
                    case 'pronouns-gender-masc': return hasFeature(word.features, 'Gender', 'Masc');
                    case 'pronouns-gender-neut': return hasFeature(word.features, 'Gender', 'Neut');
                    case 'pronouns-animacy-anim': return hasFeature(word.features, 'Animacy', 'Anim');
                    case 'pronouns-animacy-inan': return hasFeature(word.features, 'Animacy', 'Inan');
                    case 'pronouns-number-sing': return hasFeature(word.features, 'Number', 'Sing');
                    case 'pronouns-number-plur': return hasFeature(word.features, 'Number', 'Plur');
                    case 'pronouns-case-nom': return hasFeature(word.features, 'Case', 'Nom');
                    case 'pronouns-case-gen': return hasFeature(word.features, 'Case', 'Gen');
                    case 'pronouns-case-acc': return hasFeature(word.features, 'Case', 'Acc');
                    case 'pronouns-case-dat': return hasFeature(word.features, 'Case', 'Dat');
                    case 'pronouns-case-ins': return hasFeature(word.features, 'Case', 'Ins');
                    case 'pronouns-case-loc': return hasFeature(word.features, 'Case', 'Loc');
                    case 'pronouns-person-1': return hasFeature(word.features, 'Person', '1');
                    case 'pronouns-person-2': return hasFeature(word.features, 'Person', '2');
                    case 'pronouns-person-3': return hasFeature(word.features, 'Person', '3');
                    case 'pronouns-prontype-prs': return hasFeature(word.features, 'PronType', 'Prs');
                    case 'pronouns-prontype-ind': return hasFeature(word.features, 'PronType', 'Ind');
                    case 'pronouns-prontype-dem': return hasFeature(word.features, 'PronType', 'Dem');
                    case 'pronouns-prontype-intrel': return hasFeature(word.features, 'PronType', 'Int,Rel');
                    case 'pronouns-prontype-tot': return hasFeature(word.features, 'PronType', 'Tot');
                    case 'all':
                    default: return true;
                }

            case 'adjectives':
                if (word.upos !== 'ADJ') return false;
                switch (filter.option) {
                    case 'adjectives-gender-fem': return hasFeature(word.features, 'Gender', 'Fem');
                    case 'adjectives-gender-masc': return hasFeature(word.features, 'Gender', 'Masc');
                    case 'adjectives-gender-neut': return hasFeature(word.features, 'Gender', 'Neut');
                    case 'adjectives-animacy-anim': return hasFeature(word.features, 'Animacy', 'Anim');
                    case 'adjectives-animacy-inan': return hasFeature(word.features, 'Animacy', 'Inan');
                    case 'adjectives-number-sing': return hasFeature(word.features, 'Number', 'Sing');
                    case 'adjectives-number-plur': return hasFeature(word.features, 'Number', 'Plur');
                    case 'adjectives-case-nom': return hasFeature(word.features, 'Case', 'Nom');
                    case 'adjectives-case-gen': return hasFeature(word.features, 'Case', 'Gen');
                    case 'adjectives-case-acc': return hasFeature(word.features, 'Case', 'Acc');
                    case 'adjectives-case-dat': return hasFeature(word.features, 'Case', 'Dat');
                    case 'adjectives-case-ins': return hasFeature(word.features, 'Case', 'Ins');
                    case 'adjectives-case-loc': return hasFeature(word.features, 'Case', 'Loc');
                    case 'adjectives-degree-pos': return hasFeature(word.features, 'Degree', 'Pos');
                    case 'adjectives-degree-cmp': return hasFeature(word.features, 'Degree', 'Cmp');
                    case 'adjectives-degree-sup': return hasFeature(word.features, 'Degree', 'Sup');
                    case 'adjectives-form-short': return hasFeature(word.features, 'Form', 'Short');
                    case 'all':
                    default: return true;
                }

            case 'numerals':
                if (word.upos !== 'NUM') return false;
                switch (filter.option) {
                    case 'numerals-gender-fem': return hasFeature(word.features, 'Gender', 'Fem');
                    case 'numerals-gender-masc': return hasFeature(word.features, 'Gender', 'Masc');
                    case 'numerals-gender-neut': return hasFeature(word.features, 'Gender', 'Neut');
                    case 'numerals-number-sing': return hasFeature(word.features, 'Number', 'Sing');
                    case 'numerals-number-plur': return hasFeature(word.features, 'Number', 'Plur');
                    case 'numerals-case-nom': return hasFeature(word.features, 'Case', 'Nom');
                    case 'numerals-case-gen': return hasFeature(word.features, 'Case', 'Gen');
                    case 'numerals-case-acc': return hasFeature(word.features, 'Case', 'Acc');
                    case 'numerals-case-dat': return hasFeature(word.features, 'Case', 'Dat');
                    case 'numerals-case-ins': return hasFeature(word.features, 'Case', 'Ins');
                    case 'numerals-case-loc': return hasFeature(word.features, 'Case', 'Loc');
                    case 'numerals-numtype-card': return hasFeature(word.features, 'NumType', 'Card');
                    case 'all':
                    default: return true;
                }

            case 'adverbs':
                if (word.upos !== 'ADV') return false;
                switch (filter.option) {
                    case 'adverbs-degree-cmp': return hasFeature(word.features, 'Degree', 'Cmp');
                    case 'adverbs-degree-sup': return hasFeature(word.features, 'Degree', 'Sup');
                    case 'all':
                    default: return true;
                }

            case 'verbs':
                if (word.upos !== 'VERB') return false;
                switch (filter.option) {
                    case 'verbs-aspect-imp': return hasFeature(word.features, 'Aspect', 'Imp');
                    case 'verbs-aspect-perf': return hasFeature(word.features, 'Aspect', 'Perf');
                    case 'verbs-tense-past': return hasFeature(word.features, 'Tense', 'Past');
                    case 'verbs-tense-pres': return hasFeature(word.features, 'Tense', 'Pres');
                    case 'verbs-tense-fut': return hasFeature(word.features, 'Tense', 'Fut');
                    case 'verbs-mood-ind': return hasFeature(word.features, 'Mood', 'Ind');
                    case 'verbs-mood-cnd': return hasFeature(word.features, 'Mood', 'Cnd');
                    case 'verbs-mood-imp': return hasFeature(word.features, 'Mood', 'Imp');
                    case 'verbs-gender-fem': return hasFeature(word.features, 'Gender', 'Fem');
                    case 'verbs-gender-masc': return hasFeature(word.features, 'Gender', 'Masc');
                    case 'verbs-gender-neut': return hasFeature(word.features, 'Gender', 'Neut');
                    case 'verbs-number-sing': return hasFeature(word.features, 'Number', 'Sing');
                    case 'verbs-number-plur': return hasFeature(word.features, 'Number', 'Plur');
                    case 'verbs-person-1': return hasFeature(word.features, 'Person', '1');
                    case 'verbs-person-2': return hasFeature(word.features, 'Person', '2');
                    case 'verbs-person-3': return hasFeature(word.features, 'Person', '3');
                    case 'verbs-verbform-fin': return hasFeature(word.features, 'VerbForm', 'Fin');
                    case 'verbs-verbform-inf': return hasFeature(word.features, 'VerbForm', 'Inf');
                    case 'verbs-verbform-conv': return hasFeature(word.features, 'VerbForm', 'Conv');
                    case 'verbs-verbform-part': return hasFeature(word.features, 'VerbForm', 'Part');
                    case 'verbs-voice-act': return hasFeature(word.features, 'Voice', 'Act');
                    case 'verbs-voice-mid': return hasFeature(word.features, 'Voice', 'Mid');
                    case 'verbs-voice-pass': return hasFeature(word.features, 'Voice', 'Pass');
                    case 'all':
                    default: return true;
                }

            case 'prepositions':
                return word.upos === 'ADP';

            case 'particles':
                return word.upos === 'PART';

            case 'conjunctions':
                return word.upos === 'CCONJ';

            case 'subConjunctions':
                return word.upos === 'SCONJ';

            default:
                return false;
        }
    }

    // --- Format text ---

    const formatText = (json: any, currentFilters: FilterItem[]) => {
        let resultAllText = '';
        if (json && json.length > 0) {
            let paragraphNum = json[0].paragraph;
            let sentenceNum = json[0].sentence;
            resultAllText = '<p>';
            let prevEndChar = 0;

            for (let i = 0; i < json.length; i++) {
                if (parseInt(json[i].sentence) != parseInt(sentenceNum)) {
                    resultAllText += ' ';
                    sentenceNum = json[i].sentence;
                }
                if (parseInt(json[i].paragraph) != parseInt(paragraphNum)) {
                    resultAllText += '</p><p>';
                    paragraphNum = json[i].paragraph;
                }
                if (prevEndChar != 0 && (prevEndChar < parseInt(json[i].start_char))) {
                    resultAllText += ' ';
                }

                let matchingFilter: FilterItem | null = null;
                for (const filter of currentFilters) {
                    if (wordMatchesFilter(json[i], filter)) {
                        matchingFilter = filter;
                    }
                }

                if (matchingFilter) {
                    let style = '';
                    let classProps = '';
                    if (matchingFilter.color) {
                        style += 'color:' + decodeColor(matchingFilter.color) + ';';
                    }
                    if (matchingFilter.size) {
                        style += 'font-size:' + decodeSize(matchingFilter.size) + ';';
                        classProps = 'text-' + matchingFilter.size;
                    }
                    if (matchingFilter.bold) {
                        style += 'font-weight: bold;';
                    }
                    if (matchingFilter.italic) {
                        style += 'font-style: italic;';
                    }
                    if (matchingFilter.underline) {
                        style += 'text-decoration: underline;';
                    }
                    resultAllText += '<span class="' + classProps + '" style="' + style + '">' + json[i].word + '</span>';
                } else {
                    resultAllText += json[i].word;
                }

                prevEndChar = json[i].end_char;
            }
            resultAllText += '</p>';
        }
        setAllText(resultAllText);
        props.saveFormattedTextCallback(resultAllText);
    }

    // --- Filter CRUD ---

    const addFilter = (category: string, option: string) => {
        const newFilter: FilterItem = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            category,
            option,
            color: '',
            size: '',
            bold: false,
            italic: false,
            underline: false,
        };
        const newFilters = [...filters, newFilter];
        setFilters(newFilters);
        setShowPicker(false);
        setExpandedPickerGroup(null);
        setExpandedPickerSubgroup(null);
        registerNlpStart(props.task_id, 'Word Categories', category, option);
        formatText(props.analyzedText.main, newFilters);
    };

    const removeFilter = (id: string) => {
        const newFilters = filters.filter(f => f.id !== id);
        setFilters(newFilters);
        formatText(props.analyzedText.main, newFilters);
    };

    const updateFilter = (id: string, updates: Partial<FilterItem>) => {
        const newFilters = filters.map(f => f.id === id ? { ...f, ...updates } : f);
        setFilters(newFilters);
        formatText(props.analyzedText.main, newFilters);
    };

    const handleColorChange = (filterId: string, color: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Color', filter.category, color);
        }
        updateFilter(filterId, { color });
    };

    const handleSizeChange = (filterId: string, size: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Size', filter.category, size);
        }
        updateFilter(filterId, { size });
    };

    const handleBoldToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Bold', filter.category, !filter.bold ? 'yes' : 'no');
            updateFilter(filterId, { bold: !filter.bold });
        }
    };

    const handleItalicToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Italic', filter.category, !filter.italic ? 'yes' : 'no');
            updateFilter(filterId, { italic: !filter.italic });
        }
    };

    const handleUnderlineToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Underline', filter.category, !filter.underline ? 'yes' : 'no');
            updateFilter(filterId, { underline: !filter.underline });
        }
    };

    // --- Render helpers ---

    const colorOptions = (filterId: string, activeColor: string) => {
        const colors = ['blue-violet', 'brown', 'coral', 'dark-blue', 'dark-cyan', 'crimson',
            'dark-green', 'dark-salmon', 'dark-orange', 'cadet-blue', 'chocolate', 'dark-magenta'];
        return (
            <div className="color-selector">
                {colors.map(color => (
                    <span key={color}
                        className={'color-item ' + color + (activeColor === color ? ' active' : '')}
                        onClick={() => handleColorChange(filterId, color)}></span>
                ))}
            </div>
        );
    };

    const sizeOptions = (filterId: string, activeSize: string) => {
        return (
            <div className="size-selector">
                <span className={'size-item normal' + (activeSize === 'normal' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'normal')}>{'A'}</span>
                <span className={'size-item big' + (activeSize === 'big' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'big')}>{'A'}</span>
                <span className={'size-item huge' + (activeSize === 'huge' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'huge')}>{'A'}</span>
            </div>
        );
    };

    const getFilterLabel = (category: string, option: string): string => {
        const labels: Record<string, Record<string, string>> = {
            nouns: {
                'all': t("All common nouns"),
                'nouns-gender-fem': t("Feminine") + ' ' + t("Nouns").toLowerCase(),
                'nouns-gender-masc': t("Masculine") + ' ' + t("Nouns").toLowerCase(),
                'nouns-gender-neut': t("Neuter") + ' ' + t("Nouns").toLowerCase(),
                'nouns-animacy-anim': t("Animate") + ' ' + t("Nouns").toLowerCase(),
                'nouns-animacy-inan': t("Inanimate") + ' ' + t("Nouns").toLowerCase(),
                'nouns-number-sing': t("Singular") + ' ' + t("Nouns").toLowerCase(),
                'nouns-number-plur': t("Plural") + ' ' + t("Nouns").toLowerCase(),
                'nouns-case-nom': t("Nominative") + ' ' + t("Nouns").toLowerCase(),
                'nouns-case-gen': t("Genitive") + ' ' + t("Nouns").toLowerCase(),
                'nouns-case-acc': t("Accusative") + ' ' + t("Nouns").toLowerCase(),
                'nouns-case-dat': t("Dative") + ' ' + t("Nouns").toLowerCase(),
                'nouns-case-ins': t("Instrumental") + ' ' + t("Nouns").toLowerCase(),
                'nouns-case-loc': t("Locative") + ' ' + t("Nouns").toLowerCase(),
            },
            properNouns: {
                'all': t("All proper nouns"),
                'properNouns-gender-fem': t("Feminine") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-gender-masc': t("Masculine") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-gender-neut': t("Neuter") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-animacy-anim': t("Animate") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-animacy-inan': t("Inanimate") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-number-sing': t("Singular") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-number-plur': t("Plural") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-case-nom': t("Nominative") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-case-gen': t("Genitive") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-case-acc': t("Accusative") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-case-dat': t("Dative") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-case-ins': t("Instrumental") + ' ' + t("Proper nouns").toLowerCase(),
                'properNouns-case-loc': t("Locative") + ' ' + t("Proper nouns").toLowerCase(),
            },
            pronouns: {
                'all': t("All pronouns"),
                'pronouns-gender-fem': t("Feminine") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-gender-masc': t("Masculine") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-gender-neut': t("Neuter") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-animacy-anim': t("Animate") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-animacy-inan': t("Inanimate") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-number-sing': t("Singular") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-number-plur': t("Plural") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-case-nom': t("Nominative") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-case-gen': t("Genitive") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-case-acc': t("Accusative") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-case-dat': t("Dative") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-case-ins': t("Instrumental") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-case-loc': t("Locative") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-person-1': t("1st person") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-person-2': t("2nd person") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-person-3': t("3rd person") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-prontype-prs': t("Personal") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-prontype-ind': t("Indefinite") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-prontype-dem': t("Demonstrative") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-prontype-intrel': t("Interrogative/Relative") + ' ' + t("Pronouns").toLowerCase(),
                'pronouns-prontype-tot': t("Total") + ' ' + t("Pronouns").toLowerCase(),
            },
            adjectives: {
                'all': t("All adjectives"),
                'adjectives-gender-fem': t("Feminine") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-gender-masc': t("Masculine") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-gender-neut': t("Neuter") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-animacy-anim': t("Animate") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-animacy-inan': t("Inanimate") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-number-sing': t("Singular") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-number-plur': t("Plural") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-case-nom': t("Nominative") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-case-gen': t("Genitive") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-case-acc': t("Accusative") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-case-dat': t("Dative") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-case-ins': t("Instrumental") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-case-loc': t("Locative") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-degree-pos': t("Positive") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-degree-cmp': t("Comparative") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-degree-sup': t("Superlative") + ' ' + t("Adjectives").toLowerCase(),
                'adjectives-form-short': t("Short form") + ' ' + t("Adjectives").toLowerCase(),
            },
            numerals: {
                'all': t("All numerals"),
                'numerals-gender-fem': t("Feminine") + ' ' + t("Numerals").toLowerCase(),
                'numerals-gender-masc': t("Masculine") + ' ' + t("Numerals").toLowerCase(),
                'numerals-gender-neut': t("Neuter") + ' ' + t("Numerals").toLowerCase(),
                'numerals-number-sing': t("Singular") + ' ' + t("Numerals").toLowerCase(),
                'numerals-number-plur': t("Plural") + ' ' + t("Numerals").toLowerCase(),
                'numerals-case-nom': t("Nominative") + ' ' + t("Numerals").toLowerCase(),
                'numerals-case-gen': t("Genitive") + ' ' + t("Numerals").toLowerCase(),
                'numerals-case-acc': t("Accusative") + ' ' + t("Numerals").toLowerCase(),
                'numerals-case-dat': t("Dative") + ' ' + t("Numerals").toLowerCase(),
                'numerals-case-ins': t("Instrumental") + ' ' + t("Numerals").toLowerCase(),
                'numerals-case-loc': t("Locative") + ' ' + t("Numerals").toLowerCase(),
                'numerals-numtype-card': t("Cardinal"),
            },
            adverbs: {
                'all': t("All adverbs"),
                'adverbs-degree-cmp': t("Comparative") + ' ' + t("Adverbs").toLowerCase(),
                'adverbs-degree-sup': t("Superlative") + ' ' + t("Adverbs").toLowerCase(),
            },
            verbs: {
                'all': t("All verbs"),
                'verbs-aspect-imp': t("Imperfective"),
                'verbs-aspect-perf': t("Perfective"),
                'verbs-tense-past': t("Past"),
                'verbs-tense-pres': t("Present"),
                'verbs-tense-fut': t("Future"),
                'verbs-mood-ind': t("Indicative"),
                'verbs-mood-cnd': t("Conditional"),
                'verbs-mood-imp': t("Imperative"),
                'verbs-gender-fem': t("Feminine") + ' ' + t("Verbs").toLowerCase(),
                'verbs-gender-masc': t("Masculine") + ' ' + t("Verbs").toLowerCase(),
                'verbs-gender-neut': t("Neuter") + ' ' + t("Verbs").toLowerCase(),
                'verbs-number-sing': t("Singular") + ' ' + t("Verbs").toLowerCase(),
                'verbs-number-plur': t("Plural") + ' ' + t("Verbs").toLowerCase(),
                'verbs-person-1': t("1st person"),
                'verbs-person-2': t("2nd person"),
                'verbs-person-3': t("3rd person"),
                'verbs-verbform-fin': t("Finite"),
                'verbs-verbform-inf': t("Infinitive"),
                'verbs-verbform-conv': t("Converb"),
                'verbs-verbform-part': t("Participle"),
                'verbs-voice-act': t("Active"),
                'verbs-voice-mid': t("Middle"),
                'verbs-voice-pass': t("Passive"),
            },
            prepositions: {
                'all': t("Prepositions"),
            },
            particles: {
                'all': t("Particles"),
            },
            conjunctions: {
                'all': t("Coordinating Conjunctions"),
            },
            subConjunctions: {
                'all': t("Subordinating Conjunctions"),
            },
        };
        const groupNames: Record<string, string> = {
            nouns: t("Nouns"),
            properNouns: t("Proper nouns"),
            pronouns: t("Pronouns"),
            adjectives: t("Adjectives"),
            numerals: t("Numerals"),
            adverbs: t("Adverbs"),
            verbs: t("Verbs"),
        };
        const label = labels[category]?.[option] || option;
        if (option !== 'all' && groupNames[category]) {
            return groupNames[category] + ' › ' + label;
        }
        return label;
    };

    // Helper to build subgroups for gender/animacy/number/case pattern
    const buildGenderSubgroup = (prefix: string, nlp: any, genderFem: string, genderMasc: string, genderNeut: string) => ({
        key: prefix + '-gender',
        label: t("Gender"),
        count: (nlp[genderFem] || 0) + (nlp[genderMasc] || 0) + (nlp[genderNeut] || 0),
        sections: [{ title: '', options: [
            { value: prefix + '-gender-fem', label: t("Feminine"), count: nlp[genderFem] },
            { value: prefix + '-gender-masc', label: t("Masculine"), count: nlp[genderMasc] },
            { value: prefix + '-gender-neut', label: t("Neuter"), count: nlp[genderNeut] },
        ]}]
    });

    const buildAnimacySubgroup = (prefix: string, nlp: any, animAnim: string, animInan: string) => ({
        key: prefix + '-animacy',
        label: t("Animacy"),
        count: (nlp[animAnim] || 0) + (nlp[animInan] || 0),
        sections: [{ title: '', options: [
            { value: prefix + '-animacy-anim', label: t("Animate"), count: nlp[animAnim] },
            { value: prefix + '-animacy-inan', label: t("Inanimate"), count: nlp[animInan] },
        ]}]
    });

    const buildNumberSubgroup = (prefix: string, nlp: any, numSing: string, numPlur: string) => ({
        key: prefix + '-number',
        label: t("Number"),
        count: (nlp[numSing] || 0) + (nlp[numPlur] || 0),
        sections: [{ title: '', options: [
            { value: prefix + '-number-sing', label: t("Singular"), count: nlp[numSing] },
            { value: prefix + '-number-plur', label: t("Plural"), count: nlp[numPlur] },
        ]}]
    });

    const buildCaseSubgroup = (prefix: string, nlp: any, cNom: string, cGen: string, cAcc: string, cDat: string, cIns: string, cLoc: string) => ({
        key: prefix + '-case',
        label: t("Case"),
        count: (nlp[cNom] || 0) + (nlp[cGen] || 0) + (nlp[cAcc] || 0) + (nlp[cDat] || 0) + (nlp[cIns] || 0) + (nlp[cLoc] || 0),
        sections: [{ title: '', options: [
            { value: prefix + '-case-nom', label: t("Nominative"), count: nlp[cNom] },
            { value: prefix + '-case-gen', label: t("Genitive"), count: nlp[cGen] },
            { value: prefix + '-case-acc', label: t("Accusative"), count: nlp[cAcc] },
            { value: prefix + '-case-dat', label: t("Dative"), count: nlp[cDat] },
            { value: prefix + '-case-ins', label: t("Instrumental"), count: nlp[cIns] },
            { value: prefix + '-case-loc', label: t("Locative"), count: nlp[cLoc] },
        ]}]
    });

    const getPickerGroups = () => [
        {
            key: 'nouns',
            label: t("Common nouns"),
            options: [
                { value: 'all', label: t("All common nouns"), count: nlpCategories.nounsAll },
            ],
            subgroups: [
                buildGenderSubgroup('nouns', nlpCategories, 'nounsGenderFem', 'nounsGenderMasc', 'nounsGenderNeut'),
                buildAnimacySubgroup('nouns', nlpCategories, 'nounsAnimacyAnim', 'nounsAnimacyInan'),
                buildNumberSubgroup('nouns', nlpCategories, 'nounsNumberSing', 'nounsNumberPlur'),
                buildCaseSubgroup('nouns', nlpCategories, 'nounsCaseNom', 'nounsCaseGen', 'nounsCaseAcc', 'nounsCaseDat', 'nounsCaseIns', 'nounsCaseLoc'),
            ]
        },
        {
            key: 'properNouns',
            label: t("Proper nouns"),
            options: [
                { value: 'all', label: t("All proper nouns"), count: nlpCategories.properNounsAll },
            ],
            subgroups: [
                buildGenderSubgroup('properNouns', nlpCategories, 'properNounsGenderFem', 'properNounsGenderMasc', 'properNounsGenderNeut'),
                buildAnimacySubgroup('properNouns', nlpCategories, 'properNounsAnimacyAnim', 'properNounsAnimacyInan'),
                buildNumberSubgroup('properNouns', nlpCategories, 'properNounsNumberSing', 'properNounsNumberPlur'),
                buildCaseSubgroup('properNouns', nlpCategories, 'properNounsCaseNom', 'properNounsCaseGen', 'properNounsCaseAcc', 'properNounsCaseDat', 'properNounsCaseIns', 'properNounsCaseLoc'),
            ]
        },
        {
            key: 'pronouns',
            label: t("Pronouns"),
            options: [
                { value: 'all', label: t("All pronouns"), count: nlpCategories.pronounsAll },
            ],
            subgroups: [
                buildGenderSubgroup('pronouns', nlpCategories, 'pronounsGenderFem', 'pronounsGenderMasc', 'pronounsGenderNeut'),
                buildAnimacySubgroup('pronouns', nlpCategories, 'pronounsAnimacyAnim', 'pronounsAnimacyInan'),
                buildNumberSubgroup('pronouns', nlpCategories, 'pronounsNumberSing', 'pronounsNumberPlur'),
                buildCaseSubgroup('pronouns', nlpCategories, 'pronounsCaseNom', 'pronounsCaseGen', 'pronounsCaseAcc', 'pronounsCaseDat', 'pronounsCaseIns', 'pronounsCaseLoc'),
                {
                    key: 'pronouns-person',
                    label: t("Person"),
                    count: (nlpCategories.pronounsPerson1 || 0) + (nlpCategories.pronounsPerson2 || 0) + (nlpCategories.pronounsPerson3 || 0),
                    sections: [{ title: '', options: [
                        { value: 'pronouns-person-1', label: t("1st person"), count: nlpCategories.pronounsPerson1 },
                        { value: 'pronouns-person-2', label: t("2nd person"), count: nlpCategories.pronounsPerson2 },
                        { value: 'pronouns-person-3', label: t("3rd person"), count: nlpCategories.pronounsPerson3 },
                    ]}]
                },
                {
                    key: 'pronouns-prontype',
                    label: t("Type"),
                    count: (nlpCategories.pronounsPronTypePrs || 0) + (nlpCategories.pronounsPronTypeInd || 0) + (nlpCategories.pronounsPronTypeDem || 0) + (nlpCategories.pronounsPronTypeIntRel || 0) + (nlpCategories.pronounsPronTypeTot || 0),
                    sections: [{ title: '', options: [
                        { value: 'pronouns-prontype-prs', label: t("Personal"), count: nlpCategories.pronounsPronTypePrs },
                        { value: 'pronouns-prontype-ind', label: t("Indefinite"), count: nlpCategories.pronounsPronTypeInd },
                        { value: 'pronouns-prontype-dem', label: t("Demonstrative"), count: nlpCategories.pronounsPronTypeDem },
                        { value: 'pronouns-prontype-intrel', label: t("Interrogative/Relative"), count: nlpCategories.pronounsPronTypeIntRel },
                        { value: 'pronouns-prontype-tot', label: t("Total"), count: nlpCategories.pronounsPronTypeTot },
                    ]}]
                },
            ]
        },
        {
            key: 'adjectives',
            label: t("Adjectives"),
            options: [
                { value: 'all', label: t("All adjectives"), count: nlpCategories.adjectivesAll },
            ],
            subgroups: [
                buildGenderSubgroup('adjectives', nlpCategories, 'adjectivesGenderFem', 'adjectivesGenderMasc', 'adjectivesGenderNeut'),
                buildAnimacySubgroup('adjectives', nlpCategories, 'adjectivesAnimacyAnim', 'adjectivesAnimacyInan'),
                buildNumberSubgroup('adjectives', nlpCategories, 'adjectivesNumberSing', 'adjectivesNumberPlur'),
                buildCaseSubgroup('adjectives', nlpCategories, 'adjectivesCaseNom', 'adjectivesCaseGen', 'adjectivesCaseAcc', 'adjectivesCaseDat', 'adjectivesCaseIns', 'adjectivesCaseLoc'),
                {
                    key: 'adjectives-degree',
                    label: t("Degree"),
                    count: (nlpCategories.adjectivesPositive || 0) + (nlpCategories.adjectivesComparative || 0) + (nlpCategories.adjectivesSuperlative || 0),
                    sections: [{ title: '', options: [
                        { value: 'adjectives-degree-pos', label: t("Positive"), count: nlpCategories.adjectivesPositive },
                        { value: 'adjectives-degree-cmp', label: t("Comparative"), count: nlpCategories.adjectivesComparative },
                        { value: 'adjectives-degree-sup', label: t("Superlative"), count: nlpCategories.adjectivesSuperlative },
                    ]}]
                },
                {
                    key: 'adjectives-form',
                    label: t("Form"),
                    count: nlpCategories.adjectivesFormShort || 0,
                    sections: [{ title: '', options: [
                        { value: 'adjectives-form-short', label: t("Short form"), count: nlpCategories.adjectivesFormShort },
                    ]}]
                },
            ]
        },
        {
            key: 'numerals',
            label: t("Numerals"),
            options: [
                { value: 'all', label: t("All numerals"), count: nlpCategories.numeralsAll },
            ],
            subgroups: [
                buildGenderSubgroup('numerals', nlpCategories, 'numeralsGenderFem', 'numeralsGenderMasc', 'numeralsGenderNeut'),
                buildNumberSubgroup('numerals', nlpCategories, 'numeralsNumberSing', 'numeralsNumberPlur'),
                buildCaseSubgroup('numerals', nlpCategories, 'numeralsCaseNom', 'numeralsCaseGen', 'numeralsCaseAcc', 'numeralsCaseDat', 'numeralsCaseIns', 'numeralsCaseLoc'),
                {
                    key: 'numerals-numtype',
                    label: t("Type"),
                    count: nlpCategories.numeralsNumTypeCard || 0,
                    sections: [{ title: '', options: [
                        { value: 'numerals-numtype-card', label: t("Cardinal"), count: nlpCategories.numeralsNumTypeCard },
                    ]}]
                },
            ]
        },
        {
            key: 'adverbs',
            label: t("Adverbs"),
            options: [
                { value: 'all', label: t("All adverbs"), count: nlpCategories.adverbsAll },
            ],
            subgroups: [
                {
                    key: 'adverbs-degree',
                    label: t("Degree"),
                    count: (nlpCategories.adverbsComparative || 0) + (nlpCategories.adverbsSuperlative || 0),
                    sections: [{ title: '', options: [
                        { value: 'adverbs-degree-cmp', label: t("Comparative"), count: nlpCategories.adverbsComparative },
                        { value: 'adverbs-degree-sup', label: t("Superlative"), count: nlpCategories.adverbsSuperlative },
                    ]}]
                },
            ]
        },
        {
            key: 'verbs',
            label: t("Verbs"),
            options: [
                { value: 'all', label: t("All verbs"), count: nlpCategories.verbsAll },
            ],
            subgroups: [
                {
                    key: 'verbs-aspect',
                    label: t("Aspect"),
                    count: (nlpCategories.verbsAspectImp || 0) + (nlpCategories.verbsAspectPerf || 0),
                    sections: [{ title: '', options: [
                        { value: 'verbs-aspect-imp', label: t("Imperfective"), count: nlpCategories.verbsAspectImp },
                        { value: 'verbs-aspect-perf', label: t("Perfective"), count: nlpCategories.verbsAspectPerf },
                    ]}]
                },
                {
                    key: 'verbs-tense',
                    label: t("Tense"),
                    count: (nlpCategories.verbsTensePast || 0) + (nlpCategories.verbsTensePres || 0) + (nlpCategories.verbsTenseFut || 0),
                    sections: [{ title: '', options: [
                        { value: 'verbs-tense-past', label: t("Past"), count: nlpCategories.verbsTensePast },
                        { value: 'verbs-tense-pres', label: t("Present"), count: nlpCategories.verbsTensePres },
                        { value: 'verbs-tense-fut', label: t("Future"), count: nlpCategories.verbsTenseFut },
                    ]}]
                },
                {
                    key: 'verbs-mood',
                    label: t("Mood"),
                    count: (nlpCategories.verbsMoodInd || 0) + (nlpCategories.verbsMoodCnd || 0) + (nlpCategories.verbsMoodImp || 0),
                    sections: [{ title: '', options: [
                        { value: 'verbs-mood-ind', label: t("Indicative"), count: nlpCategories.verbsMoodInd },
                        { value: 'verbs-mood-cnd', label: t("Conditional"), count: nlpCategories.verbsMoodCnd },
                        { value: 'verbs-mood-imp', label: t("Imperative"), count: nlpCategories.verbsMoodImp },
                    ]}]
                },
                buildGenderSubgroup('verbs', nlpCategories, 'verbsGenderFem', 'verbsGenderMasc', 'verbsGenderNeut'),
                buildNumberSubgroup('verbs', nlpCategories, 'verbsNumberSing', 'verbsNumberPlur'),
                {
                    key: 'verbs-person',
                    label: t("Person"),
                    count: (nlpCategories.verbsPerson1 || 0) + (nlpCategories.verbsPerson2 || 0) + (nlpCategories.verbsPerson3 || 0),
                    sections: [{ title: '', options: [
                        { value: 'verbs-person-1', label: t("1st person"), count: nlpCategories.verbsPerson1 },
                        { value: 'verbs-person-2', label: t("2nd person"), count: nlpCategories.verbsPerson2 },
                        { value: 'verbs-person-3', label: t("3rd person"), count: nlpCategories.verbsPerson3 },
                    ]}]
                },
                {
                    key: 'verbs-verbform',
                    label: t("VerbForm"),
                    count: (nlpCategories.verbsVerbFormFin || 0) + (nlpCategories.verbsVerbFormInf || 0) + (nlpCategories.verbsVerbFormConv || 0) + (nlpCategories.verbsVerbFormPart || 0),
                    sections: [{ title: '', options: [
                        { value: 'verbs-verbform-fin', label: t("Finite"), count: nlpCategories.verbsVerbFormFin },
                        { value: 'verbs-verbform-inf', label: t("Infinitive"), count: nlpCategories.verbsVerbFormInf },
                        { value: 'verbs-verbform-conv', label: t("Converb"), count: nlpCategories.verbsVerbFormConv },
                        { value: 'verbs-verbform-part', label: t("Participle"), count: nlpCategories.verbsVerbFormPart },
                    ]}]
                },
                {
                    key: 'verbs-voice',
                    label: t("Voice"),
                    count: (nlpCategories.verbsVoiceAct || 0) + (nlpCategories.verbsVoiceMid || 0) + (nlpCategories.verbsVoicePass || 0),
                    sections: [{ title: '', options: [
                        { value: 'verbs-voice-act', label: t("Active"), count: nlpCategories.verbsVoiceAct },
                        { value: 'verbs-voice-mid', label: t("Middle"), count: nlpCategories.verbsVoiceMid },
                        { value: 'verbs-voice-pass', label: t("Passive"), count: nlpCategories.verbsVoicePass },
                    ]}]
                },
            ]
        },
        {
            key: 'prepositions',
            label: t("Prepositions"),
            options: [
                { value: 'all', label: t("Prepositions"), count: nlpCategories.prepositionsAll },
            ]
        },
        {
            key: 'particles',
            label: t("Particles"),
            options: [
                { value: 'all', label: t("Particles"), count: nlpCategories.particlesAll },
            ]
        },
        {
            key: 'conjunctions',
            label: t("Coordinating Conjunctions"),
            options: [
                { value: 'all', label: t("Coordinating Conjunctions"), count: nlpCategories.conjunctionsAll },
            ]
        },
        {
            key: 'subConjunctions',
            label: t("Subordinating Conjunctions"),
            options: [
                { value: 'all', label: t("Subordinating Conjunctions"), count: nlpCategories.subConjunctionsAll },
            ]
        },
    ];

    const renderFilterCard = (filter: FilterItem) => {
        return (
            <div className="word-category" key={filter.id}>
                <div className="word-category-switch active" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{getFilterLabel(filter.category, filter.option)}</span>
                    <span
                        onClick={() => removeFilter(filter.id)}
                        style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', lineHeight: 1, padding: '0 4px' }}
                        title={t("Remove")}
                    >&times;</span>
                </div>
                <div className="options">
                    <div className="format-options">
                        {colorOptions(filter.id, filter.color)}
                        {sizeOptions(filter.id, filter.size)}
                        <div className="other-formats">
                            <span className={'bold' + (filter.bold ? ' active' : '')}
                                onClick={() => handleBoldToggle(filter.id)}></span>
                            <span className={'italic' + (filter.italic ? ' active' : '')}
                                onClick={() => handleItalicToggle(filter.id)}></span>
                            <span className={'underline' + (filter.underline ? ' active' : '')}
                                onClick={() => handleUnderlineToggle(filter.id)}></span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderFilterPicker = () => {
        const groups = getPickerGroups();
        return (
            <div className="filter-picker">
                {groups.map((group: any) => (
                    <div key={group.key} className="filter-picker-group">
                        <div
                            className={'filter-picker-group-header' + (expandedPickerGroup === group.key ? ' expanded' : '')}
                            onClick={() => { setExpandedPickerGroup(expandedPickerGroup === group.key ? null : group.key); setExpandedPickerSubgroup(null); }}
                        >
                            <span className="filter-picker-arrow">{expandedPickerGroup === group.key ? '\u25BC' : '\u25B6'}</span>
                            {group.label}
                        </div>
                        {expandedPickerGroup === group.key &&
                            <div className="filter-picker-options">
                                {group.options.map((opt: any) => (
                                    <div
                                        key={opt.value}
                                        className="filter-picker-option"
                                        onClick={() => addFilter(group.key, opt.value)}
                                    >
                                        {opt.label} ({opt.count || 0})
                                    </div>
                                ))}
                                {group.subgroups && group.subgroups.map((subgroup: any) => (
                                    <div key={subgroup.key} className="filter-picker-subgroup">
                                        <div
                                            className={'filter-picker-subgroup-header' + (expandedPickerSubgroup === subgroup.key ? ' expanded' : '')}
                                            onClick={() => setExpandedPickerSubgroup(expandedPickerSubgroup === subgroup.key ? null : subgroup.key)}
                                        >
                                            <span className="filter-picker-arrow">{expandedPickerSubgroup === subgroup.key ? '\u25BC' : '\u25B6'}</span>
                                            {subgroup.label} ({subgroup.count || 0})
                                        </div>
                                        {expandedPickerSubgroup === subgroup.key &&
                                            <div className="filter-picker-suboptions">
                                                {subgroup.sections.map((section: any) => (
                                                    <div key={section.title || 'default'}>
                                                        {section.title && <div className="filter-picker-section-title">{section.title}</div>}
                                                        {section.options.map((opt: any) => (
                                                            <div
                                                                key={opt.value}
                                                                className="filter-picker-option"
                                                                onClick={() => addFilter(group.key, opt.value)}
                                                            >
                                                                {opt.label} ({opt.count || 0})
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                ))}
            </div>
        );
    };

    // --- useEffect for NLP categories ---

    useEffect(() => {
        if (props.analyzedText.main && props.analyzedText.main.length == 0 &&
            props.analyzedText.countPhrasals && props.analyzedText.countPhrasals.length == 0 &&
            props.analyzedText.countTense && props.analyzedText.countTense.length == 0 &&
            props.analyzedText.countPragmatics && props.analyzedText.countPragmatics.length == 0) {
            setProcessingEmpty(true);
        } else {
            setProcessingEmpty(false);
            const newNlpCategories = {
                // Nouns (NOUN)
                nounsAll: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN').length,
                nounsGenderFem: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Gender', 'Fem')).length,
                nounsGenderMasc: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Gender', 'Masc')).length,
                nounsGenderNeut: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Gender', 'Neut')).length,
                nounsAnimacyAnim: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Animacy', 'Anim')).length,
                nounsAnimacyInan: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Animacy', 'Inan')).length,
                nounsNumberSing: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Number', 'Sing')).length,
                nounsNumberPlur: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Number', 'Plur')).length,
                nounsCaseNom: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Case', 'Nom')).length,
                nounsCaseGen: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Case', 'Gen')).length,
                nounsCaseAcc: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Case', 'Acc')).length,
                nounsCaseDat: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Case', 'Dat')).length,
                nounsCaseIns: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Case', 'Ins')).length,
                nounsCaseLoc: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && hasFeature(c.features, 'Case', 'Loc')).length,
                // Proper Nouns (PROPN)
                properNounsAll: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN').length,
                properNounsGenderFem: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Gender', 'Fem')).length,
                properNounsGenderMasc: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Gender', 'Masc')).length,
                properNounsGenderNeut: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Gender', 'Neut')).length,
                properNounsAnimacyAnim: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Animacy', 'Anim')).length,
                properNounsAnimacyInan: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Animacy', 'Inan')).length,
                properNounsNumberSing: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Number', 'Sing')).length,
                properNounsNumberPlur: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Number', 'Plur')).length,
                properNounsCaseNom: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Case', 'Nom')).length,
                properNounsCaseGen: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Case', 'Gen')).length,
                properNounsCaseAcc: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Case', 'Acc')).length,
                properNounsCaseDat: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Case', 'Dat')).length,
                properNounsCaseIns: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Case', 'Ins')).length,
                properNounsCaseLoc: props.analyzedText.main.filter((c: any) => c.upos === 'PROPN' && hasFeature(c.features, 'Case', 'Loc')).length,
                // Pronouns (PRON)
                pronounsAll: props.analyzedText.main.filter((c: any) => c.upos === 'PRON').length,
                pronounsGenderFem: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Gender', 'Fem')).length,
                pronounsGenderMasc: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Gender', 'Masc')).length,
                pronounsGenderNeut: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Gender', 'Neut')).length,
                pronounsAnimacyAnim: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Animacy', 'Anim')).length,
                pronounsAnimacyInan: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Animacy', 'Inan')).length,
                pronounsNumberSing: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Number', 'Sing')).length,
                pronounsNumberPlur: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Number', 'Plur')).length,
                pronounsCaseNom: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Case', 'Nom')).length,
                pronounsCaseGen: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Case', 'Gen')).length,
                pronounsCaseAcc: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Case', 'Acc')).length,
                pronounsCaseDat: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Case', 'Dat')).length,
                pronounsCaseIns: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Case', 'Ins')).length,
                pronounsCaseLoc: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Case', 'Loc')).length,
                pronounsPerson1: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Person', '1')).length,
                pronounsPerson2: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Person', '2')).length,
                pronounsPerson3: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'Person', '3')).length,
                pronounsPronTypePrs: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'PronType', 'Prs')).length,
                pronounsPronTypeInd: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'PronType', 'Ind')).length,
                pronounsPronTypeDem: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'PronType', 'Dem')).length,
                pronounsPronTypeIntRel: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'PronType', 'Int,Rel')).length,
                pronounsPronTypeTot: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c.features, 'PronType', 'Tot')).length,
                // Adjectives (ADJ)
                adjectivesAll: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ').length,
                adjectivesGenderFem: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Gender', 'Fem')).length,
                adjectivesGenderMasc: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Gender', 'Masc')).length,
                adjectivesGenderNeut: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Gender', 'Neut')).length,
                adjectivesAnimacyAnim: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Animacy', 'Anim')).length,
                adjectivesAnimacyInan: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Animacy', 'Inan')).length,
                adjectivesNumberSing: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Number', 'Sing')).length,
                adjectivesNumberPlur: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Number', 'Plur')).length,
                adjectivesCaseNom: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Case', 'Nom')).length,
                adjectivesCaseGen: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Case', 'Gen')).length,
                adjectivesCaseAcc: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Case', 'Acc')).length,
                adjectivesCaseDat: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Case', 'Dat')).length,
                adjectivesCaseIns: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Case', 'Ins')).length,
                adjectivesCaseLoc: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Case', 'Loc')).length,
                adjectivesPositive: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Degree', 'Pos')).length,
                adjectivesComparative: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Degree', 'Cmp')).length,
                adjectivesSuperlative: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Degree', 'Sup')).length,
                adjectivesFormShort: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && hasFeature(c.features, 'Form', 'Short')).length,
                // Numerals (NUM)
                numeralsAll: props.analyzedText.main.filter((c: any) => c.upos === 'NUM').length,
                numeralsGenderFem: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Gender', 'Fem')).length,
                numeralsGenderMasc: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Gender', 'Masc')).length,
                numeralsGenderNeut: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Gender', 'Neut')).length,
                numeralsNumberSing: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Number', 'Sing')).length,
                numeralsNumberPlur: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Number', 'Plur')).length,
                numeralsCaseNom: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Case', 'Nom')).length,
                numeralsCaseGen: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Case', 'Gen')).length,
                numeralsCaseAcc: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Case', 'Acc')).length,
                numeralsCaseDat: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Case', 'Dat')).length,
                numeralsCaseIns: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Case', 'Ins')).length,
                numeralsCaseLoc: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'Case', 'Loc')).length,
                numeralsNumTypeCard: props.analyzedText.main.filter((c: any) => c.upos === 'NUM' && hasFeature(c.features, 'NumType', 'Card')).length,
                // Adverbs (ADV)
                adverbsAll: props.analyzedText.main.filter((c: any) => c.upos === 'ADV').length,
                adverbsComparative: props.analyzedText.main.filter((c: any) => c.upos === 'ADV' && hasFeature(c.features, 'Degree', 'Cmp')).length,
                adverbsSuperlative: props.analyzedText.main.filter((c: any) => c.upos === 'ADV' && hasFeature(c.features, 'Degree', 'Sup')).length,
                // Verbs (VERB)
                verbsAll: props.analyzedText.main.filter((c: any) => c.upos === 'VERB').length,
                verbsAspectImp: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Aspect', 'Imp')).length,
                verbsAspectPerf: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Aspect', 'Perf')).length,
                verbsTensePast: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Tense', 'Past')).length,
                verbsTensePres: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Tense', 'Pres')).length,
                verbsTenseFut: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Tense', 'Fut')).length,
                verbsMoodInd: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Mood', 'Ind')).length,
                verbsMoodCnd: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Mood', 'Cnd')).length,
                verbsMoodImp: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Mood', 'Imp')).length,
                verbsGenderFem: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Gender', 'Fem')).length,
                verbsGenderMasc: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Gender', 'Masc')).length,
                verbsGenderNeut: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Gender', 'Neut')).length,
                verbsNumberSing: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Number', 'Sing')).length,
                verbsNumberPlur: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Number', 'Plur')).length,
                verbsPerson1: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Person', '1')).length,
                verbsPerson2: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Person', '2')).length,
                verbsPerson3: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Person', '3')).length,
                verbsVerbFormFin: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'VerbForm', 'Fin')).length,
                verbsVerbFormInf: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'VerbForm', 'Inf')).length,
                verbsVerbFormConv: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'VerbForm', 'Conv')).length,
                verbsVerbFormPart: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'VerbForm', 'Part')).length,
                verbsVoiceAct: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Voice', 'Act')).length,
                verbsVoiceMid: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Voice', 'Mid')).length,
                verbsVoicePass: props.analyzedText.main.filter((c: any) => c.upos === 'VERB' && hasFeature(c.features, 'Voice', 'Pass')).length,
                // Other categories
                prepositionsAll: props.analyzedText.main.filter((c: any) => c.upos === 'ADP').length,
                particlesAll: props.analyzedText.main.filter((c: any) => c.upos === 'PART').length,
                conjunctionsAll: props.analyzedText.main.filter((c: any) => c.upos === 'CCONJ').length,
                subConjunctionsAll: props.analyzedText.main.filter((c: any) => c.upos === 'SCONJ').length,
            };
            setNlpCategories(newNlpCategories);
            formatText(props.analyzedText.main, []);
        }
    }, []);

    // --- Render ---

    return (
        <div className="processed-text-container">
            { processingEmpty &&
                <div className="empty-processed-text">
                    <Alert key={ 'warning' } variant={ 'warning' }>
                        { t("Sorry! The result of the text analysis is either empty or some errors were found. Revise and change your text and try it again.") }
                    </Alert>
                </div>
            }
            { !processingEmpty &&
                <div className="text-filters">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => { setShowPicker(!showPicker); setExpandedPickerGroup(null); setExpandedPickerSubgroup(null); }}
                        style={{ width: '100%', marginBottom: '10px' }}
                    >
                        { showPicker ? t("Cancel") : '+ ' + t("Add formatting rule") }
                    </Button>

                    { showPicker && renderFilterPicker() }

                    { filters.map(filter => renderFilterCard(filter)) }
                </div>
            }
            <div className="text-presentation">
                <div className="text-formatted">
                    { parse(allText)}
                </div>
            </div>
        </div>
    );
}
